import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React from 'react';
import { ASSIGNABLE_MEMBER_ROLES, accountRoles } from '@/lib/account-roles';

const EMAIL_RE = /^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+$/;

function memberEditableRole(member) {
  const roles = accountRoles(member);
  if (roles.includes('EDITOR')) {
    return 'EDITOR';
  }
  if (roles.includes('USER')) {
    return 'USER';
  }
  return 'EDITOR';
}

export default function TeamMemberModal({
  mode,
  member,
  onSubmit,
  onCancel,
  isSubmitting,
}) {
  const isEdit = mode === 'edit';

  const validate = (values) => {
    const errors = {};
    if (!values.firstName?.trim()) {
      errors.firstName = 'Required';
    }
    if (!values.lastName?.trim()) {
      errors.lastName = 'Required';
    }
    if (!isEdit) {
      if (!values.accountName?.trim()) {
        errors.accountName = 'Required';
      } else if (!EMAIL_RE.test(values.accountName.trim())) {
        errors.accountName = 'Enter a valid email address';
      }
    }
    if (!values.role) {
      errors.role = 'Required';
    }
    return errors;
  };

  const initialValues = isEdit
    ? {
        accountName: member?.accountName ?? '',
        firstName: member?.firstName ?? '',
        lastName: member?.lastName ?? '',
        role: memberEditableRole(member),
      }
    : {
        accountName: '',
        firstName: '',
        lastName: '',
        role: 'EDITOR',
      };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ isValid, dirty }) => (
        <Form className="admin-form">
          {isEdit && <Field type="hidden" name="accountName" />}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="input-group">
              <label htmlFor="member-first">First Name</label>
              <Field
                id="member-first"
                name="firstName"
                type="text"
                placeholder="Jordan"
              />
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                <ErrorMessage name="firstName" />
              </div>
            </div>
            <div className="input-group">
              <label htmlFor="member-last">Last Name</label>
              <Field
                id="member-last"
                name="lastName"
                type="text"
                placeholder="Smith"
              />
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                <ErrorMessage name="lastName" />
              </div>
            </div>
          </div>
          {isEdit ? (
            <div className="input-group">
              <label>Email Address</label>
              <p className="m-0 text-sm font-bold text-[#121212]">
                {member?.accountName}
              </p>
            </div>
          ) : (
            <div className="input-group">
              <label htmlFor="member-email">Email Address</label>
              <Field
                id="member-email"
                name="accountName"
                type="email"
                placeholder="jordan@company.com"
              />
              <p className="mt-1 text-xs text-[var(--festy-muted-text)]">
                A welcome email with login instructions will be sent.
              </p>
              <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
                <ErrorMessage name="accountName" />
              </div>
            </div>
          )}
          <div className="input-group">
            <label htmlFor="member-role">Role</label>
            <Field id="member-role" name="role" component="select">
              {ASSIGNABLE_MEMBER_ROLES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Field>
            <div className="mt-1 text-sm font-bold text-[var(--destructive)]">
              <ErrorMessage name="role" />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="inline-flex min-h-11 items-center justify-center rounded-[var(--bethere-radius-md)] border-2 border-[var(--bethere-primary)] bg-transparent px-6 font-bold text-[var(--bethere-primary)] transition hover:bg-[var(--bethere-primary)]/5"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !dirty || !isValid}
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-[var(--bethere-radius-md)] bg-[var(--bethere-accent)] px-6 font-bold text-[#121212] shadow-sm transition hover:bg-[var(--bethere-secondary)] disabled:opacity-60"
            >
              <FontAwesomeIcon icon={['fas', 'floppy-disk']} />
              {isEdit ? 'Save' : 'Add Member'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
